import { createSlice } from "@reduxjs/toolkit";

export const ReindexFileStatus = Object.freeze({
  QUEUED: "queued",
  PROCESSING: "processing",
  READY: "ready",
  FAILED: "failed",
  SKIPPED: "skipped",
});

export const ReindexFileSkipReason = Object.freeze({
  DELETED: "DELETED",
  FORBIDDEN: "FORBIDDEN",
  FAILED_CHECK: "FAILED_CHECK",
  NOT_SEARCHABLE: "NOT_SEARCHABLE",
});

export const ReindexTerminalState = Object.freeze({
  SUCCESS: "success",
  PARTIAL: "partial",
  FAILED: "failed",
});

const BACKEND_TO_SLICE_STATUS = {
  started: ReindexFileStatus.PROCESSING,
  completed: ReindexFileStatus.READY,
  failed: ReindexFileStatus.FAILED,
};

const initialState = {
  isOpen: false,
  fileCount: 0,
  files: {},
  terminalState: null,
};

const applySkipped = (state, skippedFiles) => {
  for (const f of skippedFiles ?? []) {
    state.files[f.ragDocumentId] = {
      title: f.title,
      source: f.source ?? state.files[f.ragDocumentId]?.source ?? null,
      status: ReindexFileStatus.SKIPPED,
      reason: f.message ?? null,
      skipReason: f.reason ?? null,
    };
  }
};

const reindexProgressSlice = createSlice({
  name: "reindexProgress",
  initialState,
  reducers: {
    started(state, action) {
      const { fileCount, files } = action.payload;
      state.isOpen = true;
      state.fileCount = fileCount;
      state.files = {};
      state.terminalState = null;
      for (const f of files) {
        state.files[f.ragDocumentId] = {
          title: f.title,
          source: f.source ?? null,
          status: ReindexFileStatus.QUEUED,
          reason: null,
        };
      }
    },
    fileProgress(state, action) {
      const { ragDocumentId, title, source, status, reason } = action.payload;
      const sliceStatus =
        BACKEND_TO_SLICE_STATUS[status] ?? ReindexFileStatus.QUEUED;
      const existing = state.files[ragDocumentId];
      state.files[ragDocumentId] = {
        title: existing?.title ?? title,
        source: source ?? existing?.source ?? null,
        status: sliceStatus,
        reason: reason ?? null,
      };
    },
    completed(state, action) {
      const { succeededFiles, failedFiles, skippedFiles } = action.payload;
      for (const f of succeededFiles) {
        state.files[f.ragDocumentId] = {
          title: f.title,
          source: f.source ?? state.files[f.ragDocumentId]?.source ?? null,
          status: ReindexFileStatus.READY,
          reason: null,
        };
      }
      for (const f of failedFiles) {
        state.files[f.ragDocumentId] = {
          title: f.title,
          source: f.source ?? state.files[f.ragDocumentId]?.source ?? null,
          status: ReindexFileStatus.FAILED,
          reason: f.reason ?? null,
        };
      }
      state.terminalState =
        failedFiles.length === 0
          ? ReindexTerminalState.SUCCESS
          : ReindexTerminalState.PARTIAL;
      applySkipped(state, skippedFiles);
      const hasFailedOrSkipped =
        failedFiles.length > 0 || (skippedFiles ?? []).length > 0;
      state.terminalState = hasFailedOrSkipped
        ? ReindexTerminalState.PARTIAL
        : ReindexTerminalState.SUCCESS;
    },
    allFailed(state, action) {
      const { failedFiles, skippedFiles } = action.payload;
      for (const f of failedFiles) {
        state.files[f.ragDocumentId] = {
          title: f.title,
          source: f.source ?? state.files[f.ragDocumentId]?.source ?? null,
          status: ReindexFileStatus.FAILED,
          reason: f.reason ?? null,
        };
      }
      applySkipped(state, skippedFiles);
      state.terminalState = ReindexTerminalState.FAILED;
    },
    dismiss() {
      return initialState;
    },
  },
});

export const { started, fileProgress, completed, allFailed, dismiss } =
  reindexProgressSlice.actions;

export const selectReindexProgress = (state) => state.visi.reindexProgress;

export default reindexProgressSlice.reducer;
